import React, { useState, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { formatDate } from '../utils/validation';
import { getConfig, resolveFileUrl } from '../services/configManager';
import ErrorModal from './ErrorModal';

interface EstimateFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  entryDate: string;
  rate: string;
}

/** Extract YYYY-MM-DD from an ISO date-time string (UTC). */
const toDateValue = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Extract HH:MM from an ISO date-time string (UTC). */
const toTimeValue = (iso: string): string => {
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${min}`;
};

/** Build a friendly duration label from milliseconds. */
const formatDuration = (ms: number): string => {
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const minutes = totalMin % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours) parts.push(`${hours} h`);
  if (minutes) parts.push(`${minutes} min`);
  return parts.join(' ') || '0 min';
};

const EstimateFeeModal: React.FC<EstimateFeeModalProps> = ({
  isOpen,
  onClose,
  entryDate,
  rate,
}) => {
  const config = getConfig();
  const ratesEnabled = config.FEATURES.Rates?.Enabled ?? false;
  const ratesUrl = resolveFileUrl(config.ASSETS.FILES.RATES);

  const defaultDate = toDateValue(entryDate);
  const entryPlusThreeHours = new Date(new Date(entryDate).getTime() + 3 * 3600_000).toISOString();
  const defaultTime = toTimeValue(entryPlusThreeHours);

  const [exitDateVal, setExitDateVal] = useState(defaultDate);
  const [exitTimeVal, setExitTimeVal] = useState(defaultTime);
  const [error, setError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ price: number | null; duration: string } | null>(null);

  const resetState = useCallback(() => {
    setExitDateVal(defaultDate);
    setExitTimeVal(defaultTime);
    setError(null);
    setApiError(null);
    setIsLoading(false);
    setResult(null);
  }, [defaultDate, defaultTime]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    resetState();
    onClose();
  }, [isLoading, resetState, onClose]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExitDateVal(e.target.value);
    setResult(null);
    if (error) setError(null);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExitTimeVal(e.target.value);
    setResult(null);
    if (error) setError(null);
  };

  const handleCalculate = async () => {
    if (!exitDateVal || !exitTimeVal) {
      setError('Please enter an expected exit date and time.');
      return;
    }

    const exitIso = `${exitDateVal}T${exitTimeVal}:00Z`;
    const exitMs = new Date(exitIso).getTime();
    const entryMs = new Date(entryDate).getTime();

    if (isNaN(exitMs)) {
      setError('Please enter a valid exit date and time.');
      return;
    }
    if (exitMs <= entryMs) {
      setError('Expected exit must be after the entry time.');
      return;
    }

    setError(null);
    setResult(null);
    setIsLoading(true);

    try {
      const response = await apiService.calculateRate({
        rate,
        entryDate,
        exitDate: exitIso,
      });

      const durationMs = exitMs - entryMs;
      setResult({
        price: response.price,
        duration: formatDuration(durationMs),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to calculate rate.';
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={true} onClose={handleClose} className="relative z-50">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
              <Dialog.Title className="text-black font-extrabold text-lg">
                Estimate your fee
              </Dialog.Title>
              <button
                onClick={handleClose}
                className="text-text-disabled hover:text-black"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-text-disabled text-small mb-4 leading-snug">
              Enter your expected exit to estimate the parking fee for this
              ticket.
            </p>

            {/* Entry date (read-only) */}
            <div className="flex justify-between text-sm mb-4">
              <span className="text-gray-500">Entry</span>
              <span className="font-semibold">{formatDate(entryDate)}</span>
            </div>

            {/* Exit date field */}
            <div className="mb-3">
              <label className="block text-[13px] font-semibold text-black mb-1.5">
                Expected exit date
              </label>
              <input
                type="date"
                value={exitDateVal}
                min={toDateValue(entryDate)}
                onChange={handleDateChange}
                disabled={isLoading}
                className="input-field w-full"
              />
            </div>

            {/* Exit time field */}
            <div className="mb-1">
              <label className="block text-[13px] font-semibold text-black mb-1.5">
                Expected exit time
              </label>
              <input
                type="time"
                value={exitTimeVal}
                onChange={handleTimeChange}
                disabled={isLoading}
                className="input-field w-full"
              />
            </div>

            {/* Validation error */}
            {error && (
              <p className="text-red-500 text-small mt-2">{error}</p>
            )}

            {/* Calculate button */}
            <button
              onClick={handleCalculate}
              disabled={isLoading}
              className="btn-primary w-full mt-4"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Calculating…
                </span>
              ) : (
                'Calculate'
              )}
            </button>

            {/* Result area */}
            {result && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-4 text-center">
                <div className="text-[32px] font-extrabold text-black leading-none">
                  {result.price != null ? `$${result.price.toFixed(2)}` : 'No charge'}
                </div>
                <div className="text-[13px] text-gray-500 mt-2">
                  {result.price != null
                    ? `Estimated stay: ${result.duration}`
                    : `Within the free period · ${result.duration}`}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-3 text-left text-[12px] text-amber-800 leading-snug">
                  This is an estimate at the posted rate and doesn't include
                  validations - any validation is applied to your actual stay at
                  exit.
                </div>
              </div>
            )}

            {/* View rate sheet link */}
            {ratesEnabled && ratesUrl && (
              <a
                href={ratesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 mt-4 text-md font-semibold text-black bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                View rate sheet
              </a>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* API error modal */}
      {apiError && (
        <ErrorModal message={apiError} onClose={() => setApiError(null)} />
      )}
    </>
  );
};

export default EstimateFeeModal;
